import { Language } from '../types';
import { nativeExperience } from './nativeExperience';
import { commercialCopy } from './commercialCopy';

const base: Record<Language, Record<string, string>> = {
  en: {
    // Navigation & Common
    'nav.marketplace': 'Marketplace',
    'nav.applications': 'Applications',
    'nav.categories': 'Categories',
    'nav.docs': 'Help',
    'nav.pricing': 'Pricing',
    'nav.kb': 'Knowledge Base',
    'nav.blog': 'Blog & News',
    'nav.status': 'System Status',
    'nav.dashboard': 'Dashboard',
    'nav.myProducts': 'My Products',
    'nav.licenses': 'License Keys',
    'nav.subscriptions': 'Subscriptions',
    'nav.trials': 'Trials',
    'nav.invoices': 'Invoices & Billing',
    'nav.organization': 'Organization',
    'nav.support': 'Support Desk',
    'nav.developerPortal': 'Developer Studio',
    'nav.adminPortal': 'Admin Console',
    'nav.settings': 'Settings',
    'nav.cart': 'Cart',
    'nav.wishlist': 'Wishlist',
    'nav.signIn': 'Sign In',
    'nav.getStarted': 'Get Started',

    // Search & Command
    'search.placeholder': 'Search applications, SDKs, docs... (CMD+K)',
    'search.quickJump': 'Quick Jump',
    'search.noResults': 'No matching software applications found.',

    // Hero Landing
    'hero.badge': 'The BeCoder software platform',
    'hero.title': 'Professional software, delivered securely',
    'hero.subtitle': 'Discover products, download them securely, and manage licenses and team devices in one place.',
    'hero.ctaExplore': 'Explore Software Catalog',
    'hero.ctaDeveloper': 'Publish Software',
    'hero.searchPrompt': 'Search by app name, CLI command, or binary platform...',

    // Pillars
    'pillar.licensing.title': 'Cryptographic Licensing',
    'pillar.licensing.desc': 'Ed25519 asymmetric signed offline keys with hardware fingerprint binding.',
    'pillar.delivery.title': 'Instant Binary Delivery',
    'pillar.delivery.desc': 'Sub-100ms global download edge with SHA-256 automated checksum verification.',
    'pillar.mena.title': 'Native LTR & RTL',
    'pillar.mena.desc': 'First-class support for English, Arabic, and Kurdish Sorani across all interfaces.',
    'pillar.enterprise.title': 'Enterprise SAML & SCIM',
    'pillar.enterprise.desc': 'Automated employee seat provisioning, floating leases, and immutable audit logs.',

    // Marketplace
    'market.title': 'Software Marketplace',
    'market.subtitle': 'Browse trusted BeCoder applications and business tools.',
    'market.filterOS': 'Operating System',
    'market.filterModel': 'License Model',
    'market.filterPrice': 'Price Range',
    'market.sortPopular': 'Most Popular',
    'market.sortNewest': 'Newest Releases',
    'market.sortRating': 'Highest Rated',
    'market.sortPriceAsc': 'Price: Low to High',
    'market.sortPriceDesc': 'Price: High to Low',
    'market.viewGrid': 'Grid View',
    'market.viewList': 'Dense List',
    'market.buyNow': 'Buy License',
    'market.startTrial': 'Start 3-Day Trial',
    'market.perpetual': 'One-time Perpetual',
    'market.floating': 'Floating Seats',
    'market.subscription': 'Annual Subscription',

    // Details Page
    'app.verifiedPublisher': 'Verified Publisher',
    'app.downloads': 'Downloads',
    'app.rating': 'Rating',
    'app.overview': 'Overview',
    'app.requirements': 'System Specs',
    'app.releaseNotes': 'Release Notes',
    'app.reviews': 'Customer Reviews',
    'app.copyKey': 'Copy License Key',
    'app.downloadBinary': 'Download Binary',
    'app.sha256': 'SHA-256 Checksum',

    // Customer Portal
    'customer.title': 'BeCoder Customer',
    'customer.activeLicenses': 'Active Licenses',
    'customer.floatingSeats': 'Available Floating Seats',
    'customer.boundDevices': 'Bound Devices',
    'customer.unbindDevice': 'Deactivate Device',
    'customer.generateOfflineKey': 'Generate Offline Activation Code',
    'customer.copyOfflinePayload': 'Paste Hardware Request Payload:',

    // Developer Portal
    'dev.title': 'Developer Studio',
    'dev.gmv': 'Gross Merchandise Value (GMV)',
    'dev.myApps': 'My Published Apps',
    'dev.submitApp': 'Publish New Software',
    'dev.scannerTitle': 'Automated Artifact Scanner Log',
    'dev.payoutLedger': 'Payout Ledger',

    // Admin Portal
    'admin.title': 'BeCoder Admin',
    'admin.moderation': 'Moderation Queue',
    'admin.killswitch': 'Emergency License Kill-Switch',
    'admin.auditLogs': 'Immutable Audit Logs',
    'admin.approve': 'Approve Listing',
    'admin.reject': 'Reject Listing',
    'admin.revoke': 'Revoke Key Immediately',

    // AI Copilot
    'ai.title': 'BeCoder Assistant',
    'ai.promptPlaceholder': 'Ask for license or product help...',
    'ai.suggestCode': 'Generate SDK Integration Code',
    'ai.troubleshoot': 'Diagnose License Activation Error',

    // Common Actions & Notifications
    'common.copy': 'Copy',
    'common.copied': 'Copied!',
    'common.active': 'Active',
    'common.expired': 'Expired',
    'common.revoked': 'Revoked',
    'common.pending': 'Pending',
    'common.success': 'Success',
    'common.cancel': 'Cancel',
    'common.save': 'Save Changes',
    'common.close': 'Close',
    'common.loading': 'Loading...',
    'license.manual.accountlessHint': 'No customer account or contact details required.',
    'license.manual.product': 'Product',
    'license.manual.plan': 'Plan',
    'license.manual.durationDays': 'Duration (days)',
    'license.manual.deviceLimit': 'Device limit',
    'license.manual.generate': 'Generate license key',
    'license.manual.key': 'License key',
    'license.manual.copyKey': 'Copy key',

    // Commercial / Contact Sales
    'sales.title': 'Contact Sales',
    'sales.subtitle': 'Share your details and BeCoder will follow up about the software you selected.',
    'sales.cta': 'Contact Sales',
    'sales.ctaCopy': 'Copy inquiry details',
    'sales.name': 'Full name',
    'sales.email': 'Work email',
    'sales.company': 'Company (optional)',
    'sales.aboutProduct': 'About',
    'sales.message': 'How can we help?',
    'sales.bulletNoCheckout': 'Product details and pricing stay visible in the public catalog.',
    'sales.bulletManual': 'Use this when you need a sales conversation. Online purchase paths will appear on product pages when ready.',
    'sales.bulletAdmin': 'After confirmation, your software access appears in your customer portal.',
    'sales.bulletNoMailbox': 'Official sales mailboxes are not live yet — use this form to prepare your inquiry.',
    'sales.sentTitle': 'Sales draft opened',
    'sales.sentBody': 'Complete the email to reach BeCoder sales.',
    'sales.copiedTitle': 'Inquiry copied',
    'sales.copiedBody': 'Your inquiry was copied to the clipboard. Official mailboxes will be published when ready.',
    'sales.pendingTitle': 'Inquiry prepared',
    'sales.pendingBody': 'Official mailboxes are not live yet. Keep your details ready and check Contact Sales again soon.',
    'sales.mailboxPending': 'Official email addresses are not published yet. Contact Sales is the public contact method for now.',
    'sales.reach': 'Or email',
    'sales.billingLater': 'Online checkout reserved',
    'sales.billingLaterBody': 'A payment provider can be enabled later without rebuilding product pages.',
    'nav.contactSales': 'Contact Sales',
    'nav.downloads': 'Downloads',
    'nav.customers': 'Customers',
    'nav.products': 'Software',
    'nav.releases': 'Releases',
    'nav.devices': 'Devices',
    'nav.featureFlags': 'Feature flags',
    'auth.signInTitle': 'Sign in to BeCoder',
    'auth.signInSubtitle': 'Customer and platform admin portals',
    'auth.email': 'Email',
    'auth.password': 'Password',
    'auth.hint': 'Use your BeCoder account email and password.',
    'auth.backHome': 'Back home',
    'auth.forbidden': 'Your account does not have access to that portal.',
    'auth.failed': 'Login failed',
    'auth.signIn': 'Sign in',
    'auth.forgotLink': 'Forgot password?',
    'auth.registerTitle': 'Create your BeCoder account',
    'auth.registerSubtitle':
      'One organization. Multiple applications. No second registration later.',
    'auth.ownerName': 'Owner name',
    'auth.createAccount': 'Create account',
    'auth.forgotTitle': 'Reset your password',
    'auth.forgotSubtitle': 'We will send a secure reset link to your email.',
    'auth.sendReset': 'Send reset link',
    'auth.backToSignIn': 'Back to sign in',
    'auth.verifyTitle': 'Verify your email',
    'auth.verifySubtitle': 'Confirm your inbox to finish setting up BeCoder.',
    'auth.verifyBody': 'Open the message from BeCoder and tap the verification link.',
    'auth.resendVerify': 'Resend email',
    'auth.visualPreview': 'Sign in securely with your BeCoder account.',
    'auth.continueGoogle': 'Continue with Google',
    'auth.continueEmail': 'Continue with email',
    'auth.googleLoading': 'Continuing with Google…',
    'auth.googleCancelled': 'Google sign-in was cancelled.',
    'auth.googleNotConfigured': 'Google sign-in is unavailable. Use email and password.',
    'auth.googleUnavailable':
      'Google sign-in is unavailable until it is configured for this environment.',
    'auth.googleInvalidState': 'Google sign-in expired. Please try again.',
    'auth.googleUnverified': 'Your Google email must be verified to continue.',
    'auth.suspended': 'This account is suspended.',
    'auth.googleLinkConflict': 'This email is already linked to a different Google account.',
    'auth.developerDisabled': 'Developer accounts are not active in this release.',
    'auth.googleFailed': 'Google sign-in failed. Please try again.',
    'auth.googleHint':
      'Google sign-in works for existing BeCoder accounts. New accounts register with email first.',
    'auth.language': 'Language',
    'auth.adminSignInTitle': 'Admin platform',
    'auth.adminSignInSubtitle': 'Secure sign-in for authorized BeCoder administrators.',
    'auth.customerSignInLink': 'Customer sign-in',
    'auth.adminOnly': 'This sign-in is for platform administrators only. Customers use the customer sign-in page.',
    'auth.emailNotVerified': 'Verify your email before signing in.',
  },
  ar: {
    // Navigation & Common
    'nav.marketplace': 'المتجر',
    'nav.applications': 'التطبيقات',
    'nav.categories': 'الفئات',
    'nav.docs': 'المساعدة',
    'nav.pricing': 'الأسعار',
    'nav.kb': 'قاعدة المعرفة',
    'nav.blog': 'المدونة والأخبار',
    'nav.status': 'حالة النظام',
    'nav.dashboard': 'لوحة التحكم',
    'nav.myProducts': 'منتجاتي',
    'nav.licenses': 'مفاتيح التراخيص',
    'nav.subscriptions': 'الاشتراكات',
    'nav.trials': 'التجارب',
    'nav.invoices': 'الفواتير والمدفوعات',
    'nav.organization': 'المؤسسة',
    'nav.support': 'الدعم الفني',
    'nav.developerPortal': 'استوديو المطورين',
    'nav.adminPortal': 'كونسول الإدارة',
    'nav.settings': 'الإعدادات',
    'nav.cart': 'السلة',
    'nav.wishlist': 'المفضلة',
    'nav.signIn': 'تسجيل الدخول',
    'nav.getStarted': 'ابدأ الآن',

    // Search & Command
    'search.placeholder': 'ابحث عن التطبيقات والوثائق والمكتبات... (CMD+K)',
    'search.quickJump': 'الانتقال السريع',
    'search.noResults': 'لم يتم العثور على برمجيات مطابقة.',

    // Hero Landing
    'hero.badge': 'منصة BeCoder للبرامج',
    'hero.title': 'برامج احترافية بتوزيع آمن',
    'hero.subtitle': 'اكتشف المنتجات، ونزّلها بأمان، وأدر التراخيص وأجهزة فريقك من مكان واحد.',
    'hero.ctaExplore': 'استكشف كتالوج البرمجيات',
    'hero.ctaDeveloper': 'انشر برمجياتك',
    'hero.searchPrompt': 'ابحث باسم التطبيق أو أفق السطر البرمجي...',

    // Pillars
    'pillar.licensing.title': 'تراخيص مشفرة',
    'pillar.licensing.desc': 'مفاتيح غير متماثلة Ed25519 مع ربط بالبصمة الرقمية للجهاز بدون اتصال.',
    'pillar.delivery.title': 'تنزيل فوري للثنائيات',
    'pillar.delivery.desc': 'تنزيل عالمي في أقل من 100 ملي ثانية مع تحقق تلقائي من خوارزمية SHA-256.',
    'pillar.mena.title': 'دعم أصلي للعربية والكردية',
    'pillar.mena.desc': 'دعم من الدرجة الأولى للغات العربية والإنجليزية والكردية السورانية في جميع الواجهات.',
    'pillar.enterprise.title': 'ربط المؤسسات SAML & SCIM',
    'pillar.enterprise.desc': 'تزويد مقاعد الموظفين تلقائياً، تراخيص عائمة، وسجلات مراجعة غير قابلة للتغيير.',

    // Marketplace
    'market.title': 'متجر البرمجيات',
    'market.subtitle': 'تصفح تطبيقات BeCoder وأدوات الأعمال الموثوقة.',
    'market.filterOS': 'نظام التشغيل',
    'market.filterModel': 'نموذج الترخيص',
    'market.filterPrice': 'نطاق السعر',
    'market.sortPopular': 'الأكثر شعبية',
    'market.sortNewest': 'أحدث الإصدارات',
    'market.sortRating': 'الأعلى تقييماً',
    'market.sortPriceAsc': 'السعر: من الأقل للأعلى',
    'market.sortPriceDesc': 'السعر: من الأعلى للأقل',
    'market.viewGrid': 'عرض شبكي',
    'market.viewList': 'قائمة مدمجة',
    'market.buyNow': 'شراء الترخيص',
    'market.startTrial': 'تجربة مجانية لمدة 3 أيام',
    'market.perpetual': 'ترخيص دائم مدى الحياة',
    'market.floating': 'مقاعد عائمة',
    'market.subscription': 'اشتراك سنوي',

    // Details Page
    'app.verifiedPublisher': 'مطور موثوق',
    'app.downloads': 'التنزيلات',
    'app.rating': 'التقييم',
    'app.overview': 'نظرة عامة',
    'app.requirements': 'متطلبات النظام',
    'app.releaseNotes': 'سجل الإصدارات',
    'app.reviews': 'آراء العملاء',
    'app.copyKey': 'نسخ مفتاح الترخيص',
    'app.downloadBinary': 'تنزيل الملف البرمجي',
    'app.sha256': 'رمز التحقق SHA-256',

    // Customer Portal
    'customer.title': 'مساحة عمل العميل',
    'customer.activeLicenses': 'التراخيص النشطة',
    'customer.floatingSeats': 'المقاعد العائمة المتاحة',
    'customer.boundDevices': 'الأجهزة المربوطة',
    'customer.unbindDevice': 'إلغاء تفعيل الجهاز',
    'customer.generateOfflineKey': 'توليد كود التفعيل بدون إنترنت',
    'customer.copyOfflinePayload': 'التقط طلب البصمة الرقمية:',

    // Developer Portal
    'dev.title': 'استوديو المطورين',
    'dev.gmv': 'إجمالي القيمة التجارية (GMV)',
    'dev.myApps': 'تطبيقاتي المنشورة',
    'dev.submitApp': 'نشر تطبيق جديد',
    'dev.scannerTitle': 'سجل الفحص التلقائي للملفات',
    'dev.payoutLedger': 'سجل المدفوعات',

    // Admin Portal
    'admin.title': 'كونسول الإدارة للمؤسسة',
    'admin.moderation': 'قائمة المراجعة',
    'admin.killswitch': 'زر الإلغاء الفوري للتراخيص',
    'admin.auditLogs': 'سجلات المراجعة الأمنية',
    'admin.approve': 'موافقة على النشر',
    'admin.reject': 'رفض التطبيق',
    'admin.revoke': 'إلغاء المفتاح فوراً',

    // AI Copilot
    'ai.title': 'مساعد BeCoder',
    'ai.promptPlaceholder': 'اسأل عن الترخيص أو المنتج...',
    'ai.suggestCode': 'توليد كود دمج الـ SDK',
    'ai.troubleshoot': 'تشخيص خطأ تفعيل الترخيص',

    // Common Actions & Notifications
    'common.copy': 'نسخ',
    'common.copied': 'تم النسخ!',
    'common.active': 'نشط',
    'common.expired': 'منتهي',
    'common.revoked': 'ملغى',
    'common.pending': 'قيد الانتظار',
    'common.success': 'تم بنجاح',
    'common.cancel': 'إلغاء',
    'common.save': 'حفظ التغييرات',
    'common.close': 'إغلاق',
    'common.loading': 'جاري التحميل...',
    'license.manual.accountlessHint': 'لا يتطلب حساب عميل أو معلومات اتصال.',
    'license.manual.product': 'المنتج',
    'license.manual.plan': 'الخطة',
    'license.manual.durationDays': 'المدة (بالأيام)',
    'license.manual.deviceLimit': 'الحد الأقصى للأجهزة',
    'license.manual.generate': 'إنشاء مفتاح ترخيص',
    'license.manual.key': 'مفتاح الترخيص',
    'license.manual.copyKey': 'نسخ المفتاح',

    'sales.title': 'تواصل للشراء',
    'sales.subtitle': 'أرسل تفاصيلك وسيتواصل BeCoder معك بشأن البرنامج الذي اخترته.',
    'sales.cta': 'تواصل للشراء',
    'sales.ctaCopy': 'نسخ تفاصيل الطلب',
    'sales.name': 'الاسم الكامل',
    'sales.email': 'البريد المهني',
    'sales.company': 'الشركة (اختياري)',
    'sales.aboutProduct': 'بخصوص',
    'sales.message': 'كيف يمكننا المساعدة؟',
    'sales.bulletNoCheckout': 'استكشف المنتجات والأسعار في الكتالوج العام أولاً.',
    'sales.bulletManual': 'استخدم هذا عند الحاجة لمحادثة مبيعات. مسارات الشراء من صفحة المنتج ستظهر عند جاهزيتها.',
    'sales.bulletAdmin': 'بعد التأكيد، يظهر وصولك للبرنامج في بوابة العميل.',
    'sales.bulletNoMailbox': 'صناديق البريد الرسمية غير متاحة بعد — استخدم هذا النموذج لإعداد طلبك.',
    'sales.sentTitle': 'تم فتح مسودة البريد',
    'sales.sentBody': 'أكمل الرسالة للتواصل مع مبيعات BeCoder.',
    'sales.copiedTitle': 'تم نسخ الطلب',
    'sales.copiedBody': 'نُسخ طلبك إلى الحافظة. ستُنشر عناوين البريد الرسمية عند جاهزيتها.',
    'sales.pendingTitle': 'تم إعداد الطلب',
    'sales.pendingBody': 'صناديق البريد الرسمية غير متاحة بعد. احتفظ بتفاصيلك وراجع تواصل المبيعات قريبًا.',
    'sales.mailboxPending': 'عناوين البريد الرسمية غير منشورة بعد. تواصل المبيعات هو قناة الاتصال العامة حاليًا.',
    'sales.reach': 'أو راسل',
    'sales.billingLater': 'الدفع الإلكتروني مؤجل',
    'sales.billingLaterBody': 'يمكن ربط بوابة دفع لاحقًا دون إعادة بناء صفحات المنتجات.',
    'nav.contactSales': 'تواصل للشراء',
    'nav.downloads': 'التنزيلات',
    'nav.customers': 'العملاء',
    'nav.products': 'البرامج',
    'nav.releases': 'الإصدارات',
    'nav.devices': 'الأجهزة',
    'nav.featureFlags': 'خصائص المنصة',
    'auth.signInTitle': 'تسجيل الدخول إلى BeCoder',
    'auth.signInSubtitle': 'بوابات العملاء ومسؤول المنصة',
    'auth.email': 'البريد الإلكتروني',
    'auth.password': 'كلمة المرور',
    'auth.hint': 'استخدم بريد وحساب BeCoder الخاص بك.',
    'auth.backHome': 'العودة للرئيسية',
    'auth.forbidden': 'حسابك لا يملك صلاحية الوصول إلى تلك البوابة.',
    'auth.failed': 'فشل تسجيل الدخول',
    'auth.signIn': 'تسجيل الدخول',
    'auth.forgotLink': 'نسيت كلمة المرور؟',
    'auth.registerTitle': 'إنشاء حساب BeCoder',
    'auth.registerSubtitle': 'منظمة واحدة. تطبيقات متعددة. بدون تسجيل ثانٍ لاحقاً.',
    'auth.ownerName': 'اسم المالك',
    'auth.createAccount': 'إنشاء حساب',
    'auth.forgotTitle': 'إعادة تعيين كلمة المرور',
    'auth.forgotSubtitle': 'سنرسل رابط إعادة تعيين آمناً إلى بريدك.',
    'auth.sendReset': 'إرسال رابط إعادة التعيين',
    'auth.backToSignIn': 'العودة لتسجيل الدخول',
    'auth.verifyTitle': 'تحقق من بريدك',
    'auth.verifySubtitle': 'أكد صندوق الوارد لإكمال إعداد BeCoder.',
    'auth.verifyBody': 'افتح الرسالة من BeCoder واضغط رابط التحقق.',
    'auth.resendVerify': 'إعادة إرسال البريد',
    'auth.visualPreview': 'سجّل الدخول بأمان باستخدام حساب BeCoder.',
    'auth.continueGoogle': 'المتابعة مع Google',
    'auth.continueEmail': 'المتابعة بالبريد الإلكتروني',
    'auth.googleLoading': 'جارٍ المتابعة مع Google…',
    'auth.googleCancelled': 'تم إلغاء تسجيل الدخول عبر Google.',
    'auth.googleNotConfigured': 'تسجيل الدخول عبر Google غير متاح. استخدم البريد وكلمة المرور.',
    'auth.googleUnavailable': 'تسجيل الدخول عبر Google غير متاح حتى يتم إعداده لهذه البيئة.',
    'auth.googleInvalidState': 'انتهت صلاحية جلسة Google. حاول مرة أخرى.',
    'auth.googleUnverified': 'يجب أن يكون بريد Google موثّقًا للمتابعة.',
    'auth.suspended': 'هذا الحساب معلّق.',
    'auth.googleLinkConflict': 'هذا البريد مرتبط بحساب Google مختلف.',
    'auth.developerDisabled': 'حسابات المطوّرين غير مفعّلة في هذا الإصدار.',
    'auth.googleFailed': 'فشل تسجيل الدخول عبر Google. حاول مرة أخرى.',
    'auth.googleHint':
      'تسجيل Google يعمل للحسابات الموجودة. الحسابات الجديدة تُنشأ بالبريد أولاً.',
    'auth.language': 'اللغة',
    'auth.adminSignInTitle': 'منصة الإدارة',
    'auth.adminSignInSubtitle': 'تسجيل دخول آمن لمسؤولي BeCoder المخوّلين.',
    'auth.customerSignInLink': 'تسجيل دخول العملاء',
    'auth.adminOnly': 'هذا الدخول مخصص لمسؤولي المنصة. يستخدم العملاء صفحة تسجيل دخول العملاء.',
    'auth.emailNotVerified': 'تحقق من بريدك الإلكتروني قبل تسجيل الدخول.',
  },
  ckb: {
    // Navigation & Common
    'nav.marketplace': 'بازاڕی سۆفتوێر',
    'nav.applications': 'بەرنامەکان',
    'nav.categories': 'پۆلەکان',
    'nav.docs': 'یارمەتی',
    'nav.pricing': 'نرخەکان',
    'nav.kb': 'بنکەی زانیاری',
    'nav.blog': 'بلۆگ و هەواڵ',
    'nav.status': 'بارودۆخی سیستەم',
    'nav.dashboard': 'داشبۆرد',
    'nav.myProducts': 'بەرهەمەکانم',
    'nav.licenses': 'کلیلی مۆڵەتەکان',
    'nav.subscriptions': 'ئابوونەکان',
    'nav.trials': 'تاقیکردنەوەکان',
    'nav.invoices': 'فاکتۆرەکان',
    'nav.organization': 'دامەزراوە',
    'nav.support': 'پشتیوانی',
    'nav.developerPortal': 'ستۆدیۆی گەشەپێدەران',
    'nav.adminPortal': 'کۆنسۆڵی بەڕێوەبەر',
    'nav.settings': 'ڕێکخستنەکان',
    'nav.cart': 'سەبەتە',
    'nav.wishlist': 'دڵخوازەکان',
    'nav.signIn': 'چوونەژوورەوە',
    'nav.getStarted': 'دەستپێبکە',

    // Search & Command
    'search.placeholder': 'گەڕان بۆ بەرنامە، بەڵگەنامە و کلیلەکان... (CMD+K)',
    'search.quickJump': 'پەڕینەوەی خێرا',
    'search.noResults': 'هیچ سۆفتوێرێک نەدۆزرایەوە.',

    // Hero Landing
    'hero.badge': 'پلاتفۆرمی سۆفتوێری BeCoder',
    'hero.title': 'سۆفتوێری پیشەیی، بە شێوەیەکی پارێزراو',
    'hero.subtitle': 'بەرهەمەکان بدۆزەرەوە، بە پارێزراوی دایانبگرە، و مۆڵەت و ئامێرەکانی تیمەکەت بەڕێوەببە.',
    'hero.ctaExplore': 'گەڕان لە کەتەلۆگ',
    'hero.ctaDeveloper': 'بڵاوکردنەوەی سۆفتوێر',
    'hero.searchPrompt': 'گەڕان بەپێی ناوی بەرنامە یان فرمانی ترمیناڵ...',

    // Pillars
    'pillar.licensing.title': 'مۆڵەتی ڕەمزێنراو',
    'pillar.licensing.desc': 'کلیلی ئۆفلاین بە پاراستنی Ed25519 و بەستنەوە بە فینگەرپرینتی ئامێر.',
    'pillar.delivery.title': 'دابەزاندنی دەستبەجێ',
    'pillar.delivery.desc': 'دابەزاندنی باینەری جیهانی خێرا لەگەڵ پشکنینی خۆکارانەی SHA-256.',
    'pillar.mena.title': 'پشتیوانی بنەڕەتی کوردی و عەرەبی',
    'pillar.mena.desc': 'پشتیوانی تەواو بۆ زمانی کوردی سۆرانی، عەرەبی و ئینگلیزی لە تەواوی ڕوکاردا.',
    'pillar.enterprise.title': 'پەیوەندی SAML & SCIM',
    'pillar.enterprise.desc': 'دابەشکردنی خۆکارانەی مۆڵەت بۆ کارمەندان و تۆماری چاودێری پارێزراو.',

    // Marketplace
    'market.title': 'بازاڕی سۆفتوێر',
    'market.subtitle': 'بەرهەم و ئامرازە متمانەپێکراوەکانی BeCoder ببینە.',
    'market.filterOS': 'سیستەمی کارپێکردن',
    'market.filterModel': 'جۆری مۆڵەت',
    'market.filterPrice': 'مەودای نرخ',
    'market.sortPopular': 'بەناوبانگترین',
    'market.sortNewest': 'نوێترین',
    'market.sortRating': 'بەرزترین پلەبەندی',
    'market.sortPriceAsc': 'نرخ: کەم بۆ زۆر',
    'market.sortPriceDesc': 'نرخ: زۆر بۆ کەم',
    'market.viewGrid': 'دیمەنی تۆڕی',
    'market.viewList': 'دیمەنی لیستی',
    'market.buyNow': 'کڕینی مۆڵەت',
    'market.startTrial': 'تاقیکردنەوەی خۆڕایی ٣ ڕۆژە',
    'market.perpetual': 'مۆڵەتی هەمیشەیی',
    'market.floating': 'مۆڵەتی هاوبەش بۆ تیم',
    'market.subscription': 'ئابوونەی ساڵانە',

    // Details Page
    'app.verifiedPublisher': 'گەشەپێدەری بڕواپێکراو',
    'app.downloads': 'دابەزاندنەکان',
    'app.rating': 'پلەبەندی',
    'app.overview': 'پوختە',
    'app.requirements': 'پێداویستی سیستەم',
    'app.releaseNotes': 'تۆماری وەشاندنەکان',
    'app.reviews': 'پێداچوونەوەی بەکارهێنەران',
    'app.copyKey': 'کۆپیکردنی کلیلی مۆڵەت',
    'app.downloadBinary': 'دابەزاندنی باینەری',
    'app.sha256': 'کۆدی SHA-256',

    // Customer Portal
    'customer.title': 'شوێنی کاری بەکارهێنەر',
    'customer.activeLicenses': 'مۆڵەتە چالاکەکان',
    'customer.floatingSeats': 'بەکارهێنەرە بەردەستەکان',
    'customer.boundDevices': 'ئامێرە بەستراوەکان',
    'customer.unbindDevice': 'لابردنی ئامێر',
    'customer.generateOfflineKey': 'دروستکردنی کۆدی ئۆفلاین',
    'customer.copyOfflinePayload': 'زانیاری فینگەرپرینتی ئامێر دابنێ:',

    // Developer Portal
    'dev.title': 'ستۆدیۆی گەشەپێدەران',
    'dev.gmv': 'کۆی بەهای بازرگانی (GMV)',
    'dev.myApps': 'بەرنامە بڵاوکراوەکانم',
    'dev.submitApp': 'بڵاوکردنەوەی بەرنامەی نوێ',
    'dev.scannerTitle': 'تۆماری پشکنینی فایلەکان',
    'dev.payoutLedger': 'تۆماری پارەدانەکان',

    // Admin Portal
    'admin.title': 'کۆنسۆڵی بەڕێوەبەرایەتی',
    'admin.moderation': 'لیستی پێداچوونەوە',
    'admin.killswitch': 'وەستاندنی فریاکەوتن',
    'admin.auditLogs': 'تۆماری چاودێری ئاسایش',
    'admin.approve': 'پەسەندکردن',
    'admin.reject': 'ڕەتکردنەوە',
    'admin.revoke': 'هەڵوەشاندنەوەی دەستبەجێ',

    // AI Copilot
    'ai.title': 'یارمەتیدەری BeCoder',
    'ai.promptPlaceholder': 'پرسیار بکە دەربارەی مۆڵەت یان بەرهەم...',
    'ai.suggestCode': 'دروستکردنی کۆدی SDK',
    'ai.troubleshoot': 'دیاریکردنی هەڵەی مۆڵەت',

    // Common Actions & Notifications
    'common.copy': 'کۆپی',
    'common.copied': 'کۆپی کرا!',
    'common.active': 'چالاک',
    'common.expired': 'بەسەرچوو',
    'common.revoked': 'هەڵوەشاوە',
    'common.pending': 'چاوەڕوان',
    'common.success': 'سەرکەوتوو بوو',
    'common.cancel': 'هەڵوەشاندنەوە',
    'common.save': 'پاشەکەوتکردن',
    'common.close': 'داخستن',
    'common.loading': 'بارکردن...',
    'license.manual.accountlessHint': 'پێویستی بە هەژماری کڕیار یان زانیاری پەیوەندی نییە.',
    'license.manual.product': 'بەرهەم',
    'license.manual.plan': 'پلان',
    'license.manual.durationDays': 'ماوە (بە ڕۆژ)',
    'license.manual.deviceLimit': 'سنووری ئامێر',
    'license.manual.generate': 'دروستکردنی کلیلی مۆڵەت',
    'license.manual.key': 'کلیلی مۆڵەت',
    'license.manual.copyKey': 'کۆپیکردنی کلیل',

    'sales.title': 'پەیوەندی بۆ کڕین',
    'sales.subtitle': 'وردەکارییەکانت بنێرە و BeCoder دەربارەی ئەو بەرنامەیەی هەڵتبژاردووە پەیوەندیت پێوە دەکات.',
    'sales.cta': 'پەیوەندی بۆ کڕین',
    'sales.name': 'ناوی تەواو',
    'sales.email': 'ئیمەیڵی کار',
    'sales.company': 'کۆمپانیا (ئارەزوومەندانە)',
    'sales.aboutProduct': 'دەربارەی',
    'sales.message': 'چۆن یارمەتیت بدەین؟',
    'sales.bulletNoCheckout': 'سەرەتا بەرهەم و نرخ لە کەتەلۆگی گشتی ببینە.',
    'sales.bulletManual': 'کاتێک پێویستت بە گفتوگۆی فرۆشتن هەیە بەکاریبهێنە. ڕێگای کڕین لە پەڕەی بەرهەم دەردەکەوێت کاتێک ئامادە بێت.',
    'sales.bulletAdmin': 'دوای پشتڕاستکردنەوە، سۆفتوێرەکەت لە پۆرتاڵی کڕیار بەردەست دەبێت.',
    'sales.bulletNoMailbox': 'سندوقی ئیمەیڵی فەرمی هێشتا ئامادە نییە — ئەم فۆرمە بەکاربهێنە بۆ ئامادەکردنی داواکارییەکەت.',
    'sales.sentTitle': 'ڕەشنووسی ئیمەیڵ کرایەوە',
    'sales.sentBody': 'ئیمەیڵەکە تەواو بکە بۆ پەیوەندی بە فرۆشتنی BeCoder.',
    'sales.copiedTitle': 'داواکاری کۆپی کرا',
    'sales.copiedBody': 'داواکارییەکەت کۆپی کرا بۆ کلیپبۆرد. ئیمەیڵی فەرمی دەخرێتە بەردەست کاتێک ئامادە بێت.',
    'sales.pendingTitle': 'داواکاری ئامادە کرا',
    'sales.pendingBody': 'سندوقی ئیمەیڵی فەرمی هێشتا ئامادە نییە. وردەکارییەکانت بپارێزە و بەم زووانە سەیری پەیوەندی فرۆشتن بکەوە.',
    'sales.mailboxPending': 'ئیمەیڵی فەرمی هێشتا بڵاو نەکراوەتەوە. پەیوەندی فرۆشتن ئێستا ڕێگای گشتی پەیوەندییە.',
    'sales.ctaCopy': 'کۆپیکردنی وردەکاری داواکاری',
    'sales.reach': 'یان بنێرە بۆ',
    'sales.billingLater': 'پارەدانی ئۆنلاین دواخراوە',
    'sales.billingLaterBody': 'دەتوانرێت دواتر دەروازەی پارەدان ببەسترێت بەبێ دووبارە دروستکردنەوەی پەڕەکانی بەرهەم.',
    'nav.contactSales': 'پەیوەندی بۆ کڕین',
    'nav.downloads': 'داگرتنەکان',
    'nav.customers': 'کڕیارەکان',
    'nav.products': 'بەرنامەکان',
    'nav.releases': 'وەشانەکان',
    'nav.devices': 'ئامێرەکان',
    'nav.featureFlags': 'ئاڵاکانی تایبەتمەندی',
    'auth.signInTitle': 'چوونەژوورەوە بۆ BeCoder',
    'auth.signInSubtitle': 'پۆرتاڵی کڕیار و بەڕێوەبەری پلاتفۆرم',
    'auth.email': 'ئیمەیڵ',
    'auth.password': 'وشەی نهێنی',
    'auth.hint': 'ئیمەیڵ و وشەی نهێنی هەژماری BeCoder بەکاربهێنە.',
    'auth.backHome': 'گەڕانەوە بۆ سەرەتا',
    'auth.forbidden': 'هەژمارەکەت ڕێگەی چوونە ناو ئەم پۆرتاڵەی نییە.',
    'auth.failed': 'چوونەژوورەوە سەرکەوتوو نەبوو',
    'auth.signIn': 'چوونەژوورەوە',
    'auth.forgotLink': 'وشەی نهێنیت لەبیرچووە؟',
    'auth.registerTitle': 'دروستکردنی هەژماری BeCoder',
    'auth.registerSubtitle': 'یەک ڕێکخراو. چەندین بەرنامە. بەبێ تۆمارکردنی دووەم.',
    'auth.ownerName': 'ناوی خاوەن',
    'auth.createAccount': 'دروستکردنی هەژمار',
    'auth.forgotTitle': 'ڕێکخستنەوەی وشەی نهێنی',
    'auth.forgotSubtitle': 'لینکی ئاسایشی ڕێکخستنەوە بۆ ئیمەیڵت دەنێرین.',
    'auth.sendReset': 'ناردنی لینکی ڕێکخستنەوە',
    'auth.backToSignIn': 'گەڕانەوە بۆ چوونەژوورەوە',
    'auth.verifyTitle': 'ئیمەیڵەکەت پشتڕاست بکەوە',
    'auth.verifySubtitle': 'سندوقی هاتوو پشتڕاست بکە بۆ تەواوکردنی BeCoder.',
    'auth.verifyBody': 'نامەی BeCoder بکەرەوە و لینکی پشتڕاستکردنەوە لێبدە.',
    'auth.resendVerify': 'دووبارە ناردنی ئیمەیڵ',
    'auth.visualPreview': 'بە هەژماری BeCoder بە پارێزراوی بچۆ ژوورەوە.',
    'auth.continueGoogle': 'بەردەوامبوون لەگەڵ Google',
    'auth.continueEmail': 'بەردەوامبوون بە ئیمەیڵ',
    'auth.googleLoading': 'بەردەوامبوون لەگەڵ Google…',
    'auth.googleCancelled': 'چوونەژوورەوەی Google هەڵوەشایەوە.',
    'auth.googleNotConfigured': 'چوونەژوورەوەی Google بەردەست نییە. ئیمەیڵ و وشەی نهێنی بەکاربهێنە.',
    'auth.googleUnavailable': 'چوونەژوورەوەی Google بەردەست نییە تا بۆ ئەم ژینگەیە ڕێک نەخرێت.',
    'auth.googleInvalidState': 'دانیشتنی Google بەسەرچوو. دووبارە هەوڵ بدە.',
    'auth.googleUnverified': 'ئیمەیڵی Google دەبێت پشتڕاستکرابێتەوە بۆ بەردەوامبوون.',
    'auth.suspended': 'ئەم هەژمارە ڕاگیراوە.',
    'auth.googleLinkConflict': 'ئەم ئیمەیڵە پێشتر بە هەژمارێکی تری Google بەستراوە.',
    'auth.developerDisabled': 'هەژماری گەشەپێدەر لەم وەشانەدا چالاک نییە.',
    'auth.googleFailed': 'چوونەژوورەوەی Google سەرکەوتوو نەبوو. دووبارە هەوڵ بدە.',
    'auth.googleHint':
      'چوونەژوورەوەی Google بۆ هەژماری بەردەست کار دەکات. هەژماری نوێ سەرەتا بە ئیمەیڵ تۆمار دەکرێت.',
    'auth.language': 'زمان',
    'auth.adminSignInTitle': 'پلاتفۆرمی بەڕێوەبەر',
    'auth.adminSignInSubtitle': 'چوونەژوورەوەی پارێزراو بۆ بەڕێوەبەرە ڕێگەپێدراوەکانی BeCoder.',
    'auth.customerSignInLink': 'چوونەژوورەوەی کڕیار',
    'auth.adminOnly': 'ئەم بەشە تەنها بۆ بەڕێوەبەرانی پلاتفۆرمە. کڕیاران پەڕەی چوونەژوورەوەی کڕیار بەکاردەهێنن.',
    'auth.emailNotVerified': 'پێش چوونەژوورەوە ئیمەیڵەکەت پشتڕاست بکەرەوە.',
  },
};

export const translations: Record<Language, Record<string, string>> = {
  en: { ...base.en, ...nativeExperience.en, ...commercialCopy.en },
  ar: { ...base.ar, ...nativeExperience.ar, ...commercialCopy.ar },
  ckb: { ...base.ckb, ...nativeExperience.ckb, ...commercialCopy.ckb },
};
